import PropTypes from "prop-types";
import { useState, useEffect, useCallback } from "react";
import jsCookie from "js-cookie";
import moment from "moment";
import { getVariants } from "lib/api";
import Link from "next/link";
import dynamic from "next/dynamic";
import useUserAuth from "helpers/hooks/useUserAuth";
import { getTravelersString } from "helpers/formatting";
import { Media } from "solar/components/Media";
import Carousel from "components/shared/stays/Carousel";
import Button from "solar/components/Button";
import Title from "components/shared/stays/Title";
import Anchor from "solar/components/Anchor";
import AuthModal from "components/auth/AuthModal";
import Message from "components/shared/stays/Message";
import SectionRhythm from "components/shared/stays/SectionRhythm";
import AvailabilityFlexibleProduct from "components/stay/AvailabilityFlexibleProduct";
import AvailabilityFooter from "components/stay/AvailabilityFooter";
import ProductsModal from "components/stay/ProductsModal";
import styles from "components/stay/AvailabilityFlexible.module.scss";

export default function AvailabilityFlexible({
  products,
  tripID,
  tripName,
  tripTypeDescription,
  guideID,
  guidePrimaryEmployeeFirstName,
  guidePrimaryEmployeeImageUrl,
  aggregatedUnavailableDates,
  aggregatedMinNights,
  responseTime = null,
  closestTrips = [],
}) {
  const visibleProducts = products.filter((p) => p.is_visible);
  const aggregatePricingType = visibleProducts[0].pricing_type; // assume all products in a trip are the same pricing type

  const [activeProduct, setActiveProduct] = useState("");
  const [showProductsModal, setShowProductsModal] = useState(false);
  const handleProductsModalClose = () => setShowProductsModal(false);
  function handleProductsModalOpen(p) {
    setShowProductsModal(true);
    setActiveProduct(p);
  }

  const authenticated = useUserAuth();
  const [showAuth, setShowAuth] = useState(false);
  const handleOpenAuth = () => setShowAuth(true);
  const handleCloseAuth = () => setShowAuth(false);

  const ChooseTravelers = dynamic(
    import("components/shared/stays/ChooseTravelers")
  );
  const [showTravelers, setShowTravelers] = useState(false);
  const handleOpenTravelers = () => setShowTravelers(true);
  const handleCloseTravelers = () => setShowTravelers(false);

  const ChooseDates = dynamic(import("components/shared/stays/ChooseDates"));
  const [showDates, setShowDates] = useState(false);
  const handleOpenDates = () => setShowDates(true);
  const handleCloseDates = () => setShowDates(false);

  const [numTravelers, setNumTravelers] = useState({
    adults: 1,
    children: 0,
    infants: 0,
  });

  const [dates, setDates] = useState(null);
  const [allProductVariants, setAllProductVariants] = useState([]);

  const handleSetDates = useCallback(
    async (obj) => {
      if (obj?.startDate && obj?.endDate) {
        const response = await getVariants(
          products.map((product) => product.id),
          obj.startDate.format("YYYY-MM-DD"),
          obj.endDate.format("YYYY-MM-DD")
        );
        if (response.errorMsg) {
          // TODO: error message UI
          // setErrorMessage(response.errorMsg);
        } else {
          setAllProductVariants(response);
        }
      } else {
        setAllProductVariants([]);
      }
      setDates(obj);
      if (obj?.startDate && obj?.endDate) {
        jsCookie.set("start-date", obj.startDate.format("YYYY-MM-DD"), {
          expires: 7,
        });
        jsCookie.set("end-date", obj.endDate.format("YYYY-MM-DD"), {
          expires: 7,
        });
      } else {
        jsCookie.remove("start-date");
        jsCookie.remove("end-date");
      }
    },
    [products]
  );

  useEffect(() => {
    if (jsCookie.get("start-date") && jsCookie.get("end-date")) {
      handleSetDates({
        startDate: moment(jsCookie.get("start-date")),
        endDate: moment(jsCookie.get("end-date")),
      });
    }
    if (jsCookie.get("num-adults")) {
      setNumTravelers({
        adults: parseInt(jsCookie.get("num-adults"), 10),
        children: parseInt(jsCookie.get("num-children"), 10),
        infants: parseInt(jsCookie.get("num-infants"), 10),
      });
    }
  }, [handleSetDates]);

  function handleSetNumTravelers(obj) {
    setNumTravelers(obj);
    jsCookie.set("num-adults", obj.adults, { expires: 7 });
    jsCookie.set("num-children", obj.children, { expires: 7 });
    jsCookie.set("num-infants", obj.infants, { expires: 7 });
  }

  function getDatesString() {
    if (dates?.startDate && dates?.endDate) {
      if (dates.startDate.isSame(dates.endDate))
        return dates.startDate.format("MMM D, YYYY");
      return `${dates.startDate.format("MMM D")} - ${dates.endDate.format(
        "MMM D, YYYY"
      )}`;
    }
    return "Add dates";
  }

  function momentToYMD(momentObj) {
    return momentObj?.format("YYYY-MM-DD");
  }

  function fullyBookedStatus() {
    const action = authenticated ? (
      <Message
        tripID={tripID}
        tripName={tripName}
        guideID={guideID}
        guidePrimaryEmployeeFirstName={guidePrimaryEmployeeFirstName}
        guidePrimaryEmployeeImageUrl={guidePrimaryEmployeeImageUrl}
        closestTrips={closestTrips}
        responseTime={responseTime}
        key={tripID}
      >
        <Button size={1} ghost>
          Join waitlist
        </Button>
      </Message>
    ) : (
      <Button size={1} ghost onClick={handleOpenAuth}>
        Join waitlist
      </Button>
    );
    return { available: false, text: "Fully booked", action };
  }

  function datesAvailableStatus() {
    const action = (
      <div>
        <Button size={1} onClick={handleOpenTravelers}>
          Add travelers
        </Button>
      </div>
    );
    return { available: true, text: "Dates available", action };
  }

  function minStayStatus(numNights) {
    const action = (
      <div>
        <Button size={1} onClick={handleOpenDates}>
          Change dates
        </Button>
      </div>
    );
    const text = aggregatePricingType.includes("per_day")
      ? `${numNights + 1} day min.`
      : `${numNights} night min.`;
    return { available: true, text, action };
  }

  function addDatesStatus() {
    const action = (
      <div>
        <Button size={1} onClick={handleOpenDates}>
          Check availability
        </Button>
      </div>
    );
    return { available: true, text: "", action };
  }

  function bookableStatus(productID, instantlyBookable) {
    const action = authenticated ? (
      <Link
        href={`/book/stays/request?trip_id=${tripID}&start_date=${momentToYMD(
          dates.startDate
        )}&end_date=${momentToYMD(dates.endDate)}&num_adults=${
          numTravelers.adults
        }&num_children=${numTravelers.children}&num_infants=${
          numTravelers.infants
        }&products[0][quantity]=1&products[0][id]=${productID}`}
      >
        <Button size={1}>{instantlyBookable ? "Book now" : "Request"}</Button>
      </Link>
    ) : (
      <Button size={1} onClick={handleOpenAuth}>
        {instantlyBookable ? "Book now" : "Request"}
      </Button>
    );
    return { available: true, text: "", action };
  }

  function getStatus(product) {
    if (dates) {
      const { startDate, endDate } = dates; // moment dates
      if (startDate === null || endDate === null) return addDatesStatus();
      const variants = allProductVariants.filter(
        (variant) => variant.product_id === parseInt(product.id, 10)
      );
      // API returns variants including end date - remove that entry if product is per_night or per_person_per_night
      if (product.pricing_type.includes("night")) variants.pop();
      const variantIdx = variants
        .map((variant) => variant.usage_date)
        .indexOf(momentToYMD(startDate));
      if (variantIdx !== -1) {
        const minNightStay = variants[variantIdx].min_night_stay;
        // let total = 0; // rt: do we still need this?
        if (minNightStay && minNightStay > endDate.diff(startDate, "days")) {
          return minStayStatus(minNightStay);
        }
        // NOTE: assumption is every day of year is represented as a variant
        for (let i = 0; i < variants.length; i += 1) {
          if (variants[i].quantity_available === 0) {
            return fullyBookedStatus();
          }
          // total += parseFloat(variants[i].price); // rt: do we still need this?
        }
        if (numTravelers) {
          return bookableStatus(product.id, product.is_instantly_bookable);
        }
        return datesAvailableStatus();
      }
      return fullyBookedStatus();
    }
    return addDatesStatus();
  }

  const filters = (
    <div className={styles.filters}>
      <Button size={1} ghost onClick={handleOpenDates}>
        {getDatesString()}
      </Button>
      <Button size={1} ghost onClick={handleOpenTravelers}>
        {getTravelersString(numTravelers)}
      </Button>
      <Message
        tripID={tripID}
        tripName={tripName}
        guideID={guideID}
        guidePrimaryEmployeeFirstName={guidePrimaryEmployeeFirstName}
        guidePrimaryEmployeeImageUrl={guidePrimaryEmployeeImageUrl}
        closestTrips={closestTrips}
        responseTime={responseTime}
        key={tripID}
      >
        <Button size={1} ghost>
          Create a custom trip
        </Button>
      </Message>
    </div>
  );

  return (
    <>
      <div className={styles.component}>
        <Anchor id="availability" offset />
        <div className={styles.container}>
          <Title>
            {tripTypeDescription === "Lodge"
              ? "Choose your accommodation"
              : "Choose your dates"}
          </Title>
        </div>
        <Media greaterThan="md">
          <SectionRhythm>
            <div className={styles.scroll}>{filters}</div>
          </SectionRhythm>
          <SectionRhythm>
            <div className={styles.container}>
              {visibleProducts.map((product) => (
                <AvailabilityFlexibleProduct
                  key={product.id}
                  id={product.id}
                  name={product.name}
                  description={product.description}
                  price={product.price}
                  pricingType={product.pricing_type}
                  images={product.product_images}
                  status={getStatus(product)}
                  handleProductsModalOpen={handleProductsModalOpen}
                />
              ))}
            </div>
          </SectionRhythm>
        </Media>
        <Media lessThan="lg">
          <SectionRhythm>
            <div className={styles.carousel}>
              <Carousel
                slidePerLg={1}
                filters={filters}
                length={visibleProducts.length}
              >
                {visibleProducts.map((product) => (
                  <AvailabilityFlexibleProduct
                    key={product.id}
                    id={product.id}
                    name={product.name}
                    description={product.description}
                    price={product.price}
                    pricingType={product.pricing_type}
                    images={product.product_images}
                    status={getStatus(product)}
                    handleProductsModalOpen={handleProductsModalOpen}
                  />
                ))}
              </Carousel>
            </div>
          </SectionRhythm>
        </Media>
        <SectionRhythm>
          <div className={styles.container}>
            <AvailabilityFooter
              tripID={tripID}
              tripName={tripName}
              guideID={guideID}
              guidePrimaryEmployeeFirstName={guidePrimaryEmployeeFirstName}
              guidePrimaryEmployeeImageUrl={guidePrimaryEmployeeImageUrl}
              closestTrips={closestTrips}
            />
          </div>
        </SectionRhythm>
      </div>
      <ProductsModal
        visibleProducts={visibleProducts}
        showModal={showProductsModal}
        handleModalClose={handleProductsModalClose}
        activeProduct={activeProduct}
        setActiveProduct={setActiveProduct}
        tripTypeDescription={tripTypeDescription}
      />
      {!authenticated && (
        <AuthModal show={showAuth} handleClose={handleCloseAuth} />
      )}
      <ChooseDates
        show={showDates}
        handleClose={handleCloseDates}
        setDates={handleSetDates}
        dates={dates}
        unavailableDates={aggregatedUnavailableDates}
        minNights={aggregatedMinNights}
        pricingType={aggregatePricingType}
      />
      <ChooseTravelers
        show={showTravelers}
        travelers={numTravelers}
        handleClose={handleCloseTravelers}
        setNumTravelers={handleSetNumTravelers}
      />
    </>
  );
}

AvailabilityFlexible.propTypes = {
  tripID: PropTypes.string.isRequired,
  tripName: PropTypes.string.isRequired,
  tripTypeDescription: PropTypes.string.isRequired,
  guideID: PropTypes.string.isRequired,
  guidePrimaryEmployeeFirstName: PropTypes.string.isRequired,
  guidePrimaryEmployeeImageUrl: PropTypes.string.isRequired,
  aggregatedUnavailableDates: PropTypes.arrayOf(PropTypes.string),
  aggregatedMinNights: PropTypes.number,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      description: PropTypes.string,
      price: PropTypes.string,
      pricing_type: PropTypes.string,
      is_instantly_bookable: PropTypes.bool,
      product_images: PropTypes.arrayOf(
        PropTypes.shape({
          file: PropTypes.shape({
            url: PropTypes.string,
          }),
        })
      ),
    })
  ).isRequired,
  responseTime: PropTypes.number,
  closestTrips: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      slug: PropTypes.string,
      country: PropTypes.string,
      lowest_price: PropTypes.node,
      product_type: PropTypes.string,
      trip_type_description: PropTypes.string,
      is_featured: PropTypes.bool,
      image_url: PropTypes.string,
      distance: PropTypes.number,
    })
  ),
};
